import React, { useEffect } from 'react';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { detectLoyaltyIntegration, useLoyalty, Statuses } from 'src/components/loyalty/use-loyalty';

import { SmallLoader } from 'shared/components';
import { DialogContainer } from 'src/components/modals/components/simple-dialog';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';
import { useRouter } from 'next/router';
import { useFindOrCreateReferralCodeMutation } from 'types/graphql';
import useErnie from 'shared/hooks/use-ernie';
import { Connect, ReferralConnect } from './connect';
import { Terms } from './terms';
import { Pin } from './pin';

type LoyaltyProps = {
  onClose: (result?: { status: Statuses }) => void;
};

export const Loyalty = ({ onClose }: LoyaltyProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const flags = useFlags();

  const isShowReferralsEnabled = flags['pos.customers.refer-a-friend.rollout'] ?? false;

  // Check if we're on a referrals route
  const router = useRouter();
  const showErnie = useErnie();
  const isReferralsRoute = isReferralsRouteUtils(router);
  const referralCode = router.isReady ? String(router.query.code) : '';

  const [
    findOrCreateReferralCodeMutation,
    { data: findOrCreateReferralCodeData },
  ] = useFindOrCreateReferralCodeMutation();

  useEffect(() => {
    if (isShowReferralsEnabled && isReferralsRoute && referralCode) {
      const fetchReferralData = async (): Promise<void> => {
        try {
          await findOrCreateReferralCodeMutation({
            variables: {
              dispensaryId: dispensary.id,
              lspName: dispensary.dposLspName ?? '',
              code: referralCode,
            },
          });
        } catch (error) {
          // Handle error silently or log it
          showErnie('Error fetching referral data. Please try again.', 'danger');
        }
      };
      void fetchReferralData();
    }
  }, [
    isShowReferralsEnabled,
    dispensary.dposLspName,
    dispensary.id,
    findOrCreateReferralCodeMutation,
    referralCode,
    isReferralsRoute,
    showErnie,
  ]);

  const { status, setStatus, programName, programDescription, createUserAndPin, processPin, resendPin } = useLoyalty({
    initialStatus: Statuses.signup,
    integration: detectLoyaltyIntegration(dispensary),
    referralData: findOrCreateReferralCodeData?.findOrCreateReferralCode ?? null,
  });

  const handleClose = (): void => {
    onClose({ status });
  };
  return (
    <DialogContainer>
      {status === Statuses.loading && <SmallLoader />}
      {status === Statuses.signup &&
        (isShowReferralsEnabled && isReferralsRoute ? (
          <ReferralConnect
            onClose={onClose}
            setStatus={setStatus}
            programName={programName}
            programDescription={programDescription}
          />
        ) : (
          <Connect
            onClose={handleClose}
            setStatus={setStatus}
            programName={programName}
            programDescription={programDescription}
          />
        ))}

      {status === Statuses.terms && (
        <Terms onClose={handleClose} programName={programName} createUserAndPin={createUserAndPin} />
      )}
      {[Statuses.pin, Statuses.incorrectPin, Statuses.success].includes(status) && (
        <Pin
          onClose={handleClose}
          status={status}
          programName={programName}
          processPin={processPin}
          resendPin={resendPin}
        />
      )}
    </DialogContainer>
  );
};
